import { ref } from 'vue';

import { useUserStore } from '../stores/user';
import type { User } from '../stores/user';
import { useOrganizationStore } from '../stores/organization';
import type { Organization } from '../stores/organization';
import { ApiError, apiFetch, unwrap } from '../http/client';
import type { DataResult } from '../http/client';
import { applyTheme } from './useTheme';
import { redirectToError } from './useErrorRouter';

// Blocks the app shell with a loading spinner until user + organization are loaded
// (Angular AppComponent.loading). Only ever true for a logged-in user; cleared on
// success and error alike (= Angular's combineLatest subscribe + error handler).
export const isAppInitializing = ref(false);

// Angular `HttpErrorHandler.getHttpErrorForHttpErrorResponse()`: a transport failure is status 0,
// and status 0 while the browser reports itself offline becomes the internal marker 1400
// (`HTTP_STATUS_OFFLINE`). Both have their own headline on the error page.
function httpStatusOf(e: unknown): number {
    if (e instanceof ApiError) {
        return e.response.status;
    }
    return navigator.onLine === false ? 1400 : 0;
}

export async function initApp(): Promise<void> {
    const userStore = useUserStore();
    const orgStore = useOrganizationStore();

    if (!userStore.isLoggedIn || !userStore.institutionId) {
        return;
    }

    isAppInitializing.value = true;
    try {
        // Kept as two awaited promises instead of one `Promise.all` destructure: Angular handles the
        // two failures differently — `UserService.fetchUsers()` sends the user to the error page
        // (user.service.ts:88), `OrganizationService` only swallows the error. Both still start in
        // parallel; only the error paths differ.
        const usersPromise = apiFetch<DataResult<User[]>>('/users');
        const orgPromise = apiFetch<DataResult<Organization>>(`/organizations/${userStore.institutionId}/profile`);
        // Without this the org rejection would be an unhandled rejection while we await `/users`.
        orgPromise.catch(() => undefined);

        let usersResult: DataResult<User[]>;
        try {
            usersResult = await usersPromise;
        } catch (e) {
            // Angular: 401/403 log out (that part already happens inside `apiFetch`), then EVERY
            // status lands on the error page and the user list is emptied. This is the only producer
            // of the 0/401/403/500/1400 headlines there — without it the whole status matrix except
            // `404` is unreachable and a failing `/users` leaves a half-empty dashboard.
            userStore.setFamilyUsers([]);
            redirectToError(httpStatusOf(e));
            throw e;
        }
        const orgResult = await orgPromise;

        userStore.setFamilyUsers(usersResult.data);
        // Main user = profile matching the JWT `userId` (parent app: the parent among
        // parent+children); fall back to `data[0]` for single-user apps / edge cases.
        const mainUser = usersResult.data.find((u) => u.id === userStore.mainUserId) ?? usersResult.data[0];
        if (mainUser) {
            userStore.setUser(mainUser);
        }
        // Backend nests the individual institution theme as its own `{ data: … }` envelope
        // (Fractal Item include); unwrap it before applying, else every color/font key is undefined
        // and applyTheme() strips all custom properties instead of setting them.
        const theme = unwrap<Record<string, string>>(orgResult.data.theme);
        orgStore.setOrganization({ ...orgResult.data, theme });
        applyTheme(theme);
    } catch (e) {
        // Deliberately swallowed and deliberately NOT touching the session. A 401 already dropped
        // the token inside `apiFetch` — the same single place Angular's JWTTokenInterceptor does it
        // — so the router guard opens the re-auth overlay on the next guarded navigation. Anything
        // else must leave the session alone, a plain 403 above all: there the token is valid and the
        // server merely refused this resource, so a password prompt would ask for something that
        // cannot change the outcome. (`two_factor_setup_required` is redirected in `apiFetch` before
        // it reaches this catch, which is the only reason the old 403 branch had to re-read the body.)
        console.error('[useAppInit] initApp failed', e);
    } finally {
        isAppInitializing.value = false;
    }
}

/**
 * Angular `UserService.reloadUsers()`: refreshes the family list in the background. Deliberately
 * does NOT touch `isAppInitializing` — Angular shows no app-wide loading screen for this.
 */
export async function reloadUsers(): Promise<void> {
    const userStore = useUserStore();
    if (!userStore.isLoggedIn) {
        return;
    }

    try {
        let usersResult: DataResult<User[]>;
        try {
            usersResult = await apiFetch<DataResult<User[]>>('/users');
        } catch (e) {
            // Same `fetchUsers()` in Angular, same error branch.
            userStore.setFamilyUsers([]);
            redirectToError(httpStatusOf(e));
            throw e;
        }
        userStore.setFamilyUsers(usersResult.data);
        const mainUser = usersResult.data.find((u) => u.id === userStore.mainUserId) ?? usersResult.data[0];
        if (mainUser) {
            userStore.setUser(mainUser);
        }
    } catch (e) {
        // Same reasoning as initApp(): the 401 token drop lives in `apiFetch`, everything else
        // leaves the session untouched.
        console.error('[useAppInit] reloadUsers failed', e);
    }
}
